import { first, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { User } from '../../models/user';
import { Configuration, LoggedInUser } from '../../models/logged-in-user';
import { DepotLocation } from '../../models/location';
import { TaxRate } from '../../models/tax-rate';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';

@Injectable()
export class ConfigService {
  static dates = {
    dateFormat: 'DD/MM/YY',
    timeFormat: 'HH:mm',
    shortDateTimeFormat: 'DD/MM/YY HH:mm',
    longDateTimeFormat: 'Do MMM YYYY HH:mm'
  };

  private dashboardFilterConfigBs = new BehaviorSubject<{
    user: number;
    location: number;
  }>({
    user: 0,
    location: 0
  });

  dashboardFilterConfig = this.dashboardFilterConfigBs.asObservable();
  API_URL = environment.api_url;

  private currentUserSubject = new BehaviorSubject<User>(null);
  currentUser = this.currentUserSubject.asObservable();
  private locationsSubject = new BehaviorSubject<Array<DepotLocation>>([]);
  locations = this.locationsSubject.asObservable();
  private currentLocationSubject = new BehaviorSubject<DepotLocation>(null);
  currentLocation = this.currentLocationSubject.asObservable();
  private allUsersSubject = new BehaviorSubject<Array<User>>([]);
  allUsers = this.allUsersSubject.asObservable();
  allTenantUsers = this.allUsers.pipe(
    map((users) => users.filter((user) => user.user_tenant_id))
  );
  private taxRateSubject = new BehaviorSubject<Array<TaxRate>>(null);
  taxRates = this.taxRateSubject.asObservable();
  currentUserConfig: any = {};
  private userConfigSubject = new BehaviorSubject<any>(this.currentUserConfig);
  userConfig = this.userConfigSubject.asObservable();

  userLogout = new Subject<void>();

  columnConfigs = {};

  constructor(private http: HttpClient) {
    this.userLogout.subscribe(() => {
      this.onLogout();
    });
  }

  setCurrentLocation(location: DepotLocation): void {
    this.currentLocationSubject.next(location);
  }

  private setLocations(locations: DepotLocation[]) {
    this.locationsSubject.next(locations);
  }

  private setCurrentUser(user: User) {
    this.currentUserSubject.next(user);
    this.setupFreshChat(user);
  }

  private setTaxRates(taxRates: TaxRate[]) {
    this.taxRateSubject.next(taxRates);
  }

  private setAllUsers(users: User[]) {
    this.allUsersSubject.next(users);
  }

  getLoggedInUser(token): Observable<HttpResponse<LoggedInUser>> {
    let headers = new HttpHeaders();
    headers = headers.set('token', token);
    return this.http
      .get<LoggedInUser>(this.API_URL + 'loggedInUser', {
        headers: headers,
        // headers: {token: token},
        observe: 'response'
      })
      .pipe(
        tap((response) => {
          if (response.status === 200) {
            let loggedInUser: LoggedInUser = response.body as LoggedInUser;
            this.setCurrentUser(loggedInUser.user);
            this.setLocations(loggedInUser.locations);
            this.setTaxRates(loggedInUser.tax_rates);
            this.setAllUsers(loggedInUser.users);
            this.extractUserConfig(loggedInUser.config);
          }
          return response;
        })
      );
  }

  private extractUserConfig(config: Configuration[]) {
    const extracted = config.reduce((acc, curr) => {
      acc[curr.name] = curr.value;
      return acc;
    }, {});
    this.setUserConfig(extracted);
  }

  clearConfiguration() {
    this.setLocations([]);
    this.setCurrentLocation(null);
    this.setCurrentUser(null);
  }

  getLocationObject(locationId) {
    this.locations.pipe(first()).subscribe((locations) => {
      if (!locations) {
        return;
      }
      locations.forEach((locationObject) => {
        if (locationObject.id === locationId) {
          this.setCurrentLocation(locationObject);
        }
      });
    });
  }

  private setUserConfig(userConfig: any) {
    this.userConfigSubject.next(userConfig);
  }

  updateUserConfig(configKey: string, newConfig) {
    if (!this.currentUserConfig[configKey]) {
      this.currentUserConfig[configKey] = {};
    }
    this.currentUserConfig[configKey] = newConfig;
    this.setUserConfig(this.currentUserConfig);
  }

  private onLogout() {
    this.currentUserConfig = {};
    this.userConfigSubject.next({});
  }

  setupFreshChat(user: User) {
    if (window['fcWidget']) {
      if (user) {
        let identifier = user.email ? user.email : user.user_name;
        // To set unique user id in your system when it is available
        window['fcWidget'].setExternalId(identifier);
        // To set user name
        window['fcWidget'].user.setFirstName(user.name);
        // To set user email
        window['fcWidget'].user.setEmail(identifier);
      } else {
        window['fcWidget'].setExternalId(null);
        // To set user name
        window['fcWidget'].user.setFirstName(null);
        // To set user email
        window['fcWidget'].user.setEmail(null);
      }
    }
  }

  setDashboardConfig(update: { user: number; location: number }) {
    this.dashboardFilterConfigBs.next(update);
  }
}
